'use client';

import { formatDate, formatRelativeTime, formatTimeAgo } from '@bridge/utils';

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './Tooltip';

const shortDateOptions: Intl.DateTimeFormatOptions = {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
};

// Used by the default (non-displayLocalAsPrimary) date-only branch. Forces UTC so that
// date-only strings like "2026-05-01" (parsed as UTC midnight) don't shift back a day
// in western timezones. This is correct for all API date values — they are UTC.
const shortDateUtcOptions: Intl.DateTimeFormatOptions = {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  timeZone: 'UTC',
};

const shortDateTimeOptions: Intl.DateTimeFormatOptions = {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
  timeZone: 'UTC',
};

const shortDateTimeWithSecondsOptions: Intl.DateTimeFormatOptions = {
  ...shortDateTimeOptions,
  second: '2-digit',
};

const fullUtcOptions: Intl.DateTimeFormatOptions = {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  second: '2-digit',
  hour12: true,
  timeZone: 'UTC',
};

const fullLocalOptions: Intl.DateTimeFormatOptions = {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  second: '2-digit',
  hour12: true,
};

export interface DateDisplayProps {
  /** ISO date string or Date object */
  date: string | Date;
  /** Show time in addition to date (e.g. "Mar 26, 2026, 2:30 PM UTC") */
  showTime?: boolean;
  /** Show relative time (e.g. "in 47 minutes") with full date in tooltip. Falls back to absolute date if in the past. */
  relative?: boolean;
  /** Prefix for relative display (e.g. "Expires" → "Expires in 47 minutes") */
  relativePrefix?: string;
  /** Text to show when relative date is in the past (e.g. "Expired"). Defaults to absolute date. */
  relativePastLabel?: string;
  /** If true, display local time as the primary text and UTC in the tooltip (flips the default) */
  displayLocalAsPrimary?: boolean;
  /**
   * Include seconds in the primary UTC time text (default is minute precision). Applies to
   * the default `showTime` path only; the `displayLocalAsPrimary` path already shows seconds.
   */
  showSeconds?: boolean;
}

/**
 * Displays a formatted date with a tooltip showing the timezone(s) the primary text omits:
 * only local time when the primary is already a full UTC timestamp, otherwise both UTC and local.
 */
export function DateDisplay({ date, showTime, showSeconds, relative, relativePrefix, relativePastLabel, displayLocalAsPrimary }: DateDisplayProps) {
  const dateObj = typeof date === 'string' ? new Date(date) : date;

  // Detect local timezone abbreviation
  const tzName = Intl.DateTimeFormat('en-us', { timeZoneName: 'short' })
    .formatToParts(dateObj)
    .find((p) => p.type === 'timeZoneName')?.value ?? '';

  const utcText = formatDate(dateObj, fullUtcOptions) + ' UTC';
  const localText = formatDate(dateObj, fullLocalOptions) + ' ' + tzName;

  let displayText: string;
  if (relative) {
    const relativeText = formatRelativeTime(dateObj);
    if (relativeText) {
      displayText = relativePrefix ? `${relativePrefix} ${relativeText}` : relativeText;
    } else if (relativePastLabel) {
      displayText = relativePastLabel;
    } else {
      displayText = formatTimeAgo(dateObj);
    }
  } else if (displayLocalAsPrimary) {
    displayText = showTime ? localText : formatDate(dateObj, shortDateOptions);
  } else {
    displayText = showTime
      ? formatDate(dateObj, showSeconds ? shortDateTimeWithSecondsOptions : shortDateTimeOptions) + ' UTC'
      : formatDate(dateObj, shortDateUtcOptions);
  }

  // The tooltip surfaces the zone(s) the primary text doesn't already show. When the primary
  // is a full UTC timestamp, repeating UTC adds nothing — show only local. When it omits the
  // time (date-only or relative), show both zones so the tooltip adds the full timestamp.
  const primaryShowsUtcTime = !relative && !displayLocalAsPrimary && showTime;
  const zoneTexts = displayLocalAsPrimary ? [utcText] : primaryShowsUtcTime ? [localText] : [utcText, localText];
  // In a UTC browser the UTC and local timestamps are the same string, so each zone shows once.
  const tooltipLines = [...new Set(zoneTexts)];

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="cursor-default">{displayText}</span>
        </TooltipTrigger>
        <TooltipContent onClick={(e) => e.stopPropagation()}>
          <div className="flex flex-col gap-0.5 text-xs">
            {tooltipLines.map((line, index) => (
              <span className={index === 0 ? undefined : 'text-content-tertiary'} key={line}>
                {line}
              </span>
            ))}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
